import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Truck, Plus, Pencil, Trash2, X, Search, Save, FileSpreadsheet, Upload, Columns, Download, BarChart3, GitMerge, ArrowLeftRight } from "lucide-react";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { isValidVatIT, isValidIBAN } from "../lib/validators";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const EXPORT_COLS = [
  { key: "supplier_name", label: "Ragione sociale" },
  { key: "vat", label: "P. IVA" },
  { key: "tax_code", label: "Cod. Fiscale" },
  { key: "address", label: "Indirizzo" },
  { key: "cap", label: "CAP" },
  { key: "city", label: "Comune" },
  { key: "province", label: "Prov." },
  { key: "country", label: "Stato" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Telefono" },
  { key: "sdi_code", label: "Cod. SDI" },
  { key: "iban", label: "IBAN" },
  { key: "delivery_conditions", label: "Condizioni consegna" },
  { key: "payment_terms", label: "Condizioni pagamento" },
  { key: "notes", label: "Note" },
  { key: "invoice_count", label: "N. fatture" },
  { key: "total_spent", label: "Spesa €" },
];

const FIELDS = [
  { key: "supplier_name", label: "Ragione sociale", full: true, required: true },
  { key: "vat", label: "P. IVA" },
  { key: "tax_code", label: "Codice Fiscale" },
  { key: "address", label: "Indirizzo", full: true },
  { key: "cap", label: "CAP" },
  { key: "city", label: "Comune" },
  { key: "province", label: "Prov." },
  { key: "country", label: "Stato" },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Telefono" },
  { key: "sdi_code", label: "Codice Destinatario (SDI)" },
  { key: "iban", label: "IBAN" },
  { key: "delivery_conditions", label: "Condizioni di consegna", full: true },
  { key: "payment_terms", label: "Condizioni di pagamento", full: true },
  { key: "notes", label: "Note", full: true },
];

const euro = (n) => `€ ${Number(n || 0).toFixed(2)}`;

export default function SuppliersRegistry() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [creating, setCreating] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [merging, setMerging] = useState(false);
  const [showCols, setShowCols] = useState(false);
  const [selCols, setSelCols] = useState(EXPORT_COLS.map((c) => c.key));
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [detail, setDetail] = useState(null);
  const [selProd, setSelProd] = useState(null);
  const fileRef = useRef(null);

  const load = () => api.get("/suppliers").then(({ data }) => setRows(data)).catch(() => toast.error("Impossibile caricare i fornitori"));
  useEffect(() => { load(); }, []);

  const toggleCol = (k) => setSelCols((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);
  const downloadTemplate = async () => {
    try { const { data } = await api.get("/suppliers/import-template.xlsx", { responseType: "blob" }); const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = "template_fornitori.xlsx"; a.click(); URL.revokeObjectURL(u); }
    catch { toast.error("Errore download template"); }
  };
  const exportExcel = async () => {
    try {
      const params = selCols.length && selCols.length < EXPORT_COLS.length ? `?columns=${selCols.join(",")}` : "";
      const { data } = await api.get(`/suppliers.xlsx${params}`, { responseType: "blob" });
      const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = "fornitori.xlsx"; a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("Errore export"); }
  };
  const onImportPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/suppliers/import?dry_run=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview({ ...data, file });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore lettura file"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const confirmImport = async () => {
    if (!importPreview?.file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", importPreview.file);
    try {
      const { data } = await api.post("/suppliers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${data.created} nuovi, ${data.updated} aggiornati${data.skipped ? `, ${data.skipped} duplicati ignorati` : ""}`);
      setImportPreview(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore import"); }
    finally { setImporting(false); }
  };

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    const hay = `${r.supplier_name} ${r.vat} ${r.city} ${r.email}`.toLowerCase();
    return hay.includes(q.toLowerCase());
  }), [rows, q]);
  const { dupSet, dupPartner } = useMemo(() => {
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
      .replace(/\b(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?|societa|ditta)\b/g, "")
      .replace(/[^a-z0-9]/g, "");
    const groups = {};
    rows.forEach((r) => { const k = norm(r.supplier_name); if (!k) return; (groups[k] = groups[k] || []).push(r.supplier_name); });
    const set = new Set(); const partner = {};
    Object.values(groups).forEach((arr) => { if (arr.length > 1) arr.forEach((n) => { set.add(n); partner[n] = arr.find((x) => x !== n); }); });
    return { dupSet: set, dupPartner: partner };
  }, [rows]);

  const del = (r) => setConfirmDel(r);
  const doDel = async () => {
    const r = confirmDel;
    try { await api.delete(`/suppliers/${encodeURIComponent(r.supplier_name)}`); toast.success("Fornitore eliminato"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setConfirmDel(null);
  };
  const openDetail = async (name) => {
    try { const { data } = await api.get(`/suppliers/${encodeURIComponent(name)}/detail`); setDetail(data); setSelProd(data.products[0] || null); }
    catch { toast.error("Errore caricamento scheda"); }
  };

  const inp = "border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Fornitori</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Anagrafica Fornitori</h1>
          <p className="text-sm text-stone-500 mt-2">Creali manualmente o lascia che si popolino in automatico dall'import delle fatture d'acquisto.</p>
        </div>
        <div className="flex items-center gap-2 relative">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" data-testid="import-file-input" onChange={onImportPick} />
          <button data-testid="template-btn" onClick={downloadTemplate} className="rounded-full border border-stone-300 text-stone-600 px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-stone-100 transition-colors"><Download size={16} /> Template</button>
          <button data-testid="import-suppliers-btn" onClick={() => fileRef.current?.click()} className="rounded-full border border-stone-300 text-stone-600 px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-stone-100 transition-colors"><Upload size={16} /> Importa Excel</button>
          <div className="relative">
            <button data-testid="columns-btn" onClick={() => setShowCols((v) => !v)} className="rounded-full border border-stone-300 text-stone-600 px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-stone-100 transition-colors"><Columns size={16} /> Colonne</button>
            {showCols && (
              <div data-testid="columns-panel" className="absolute right-0 mt-2 w-56 max-h-72 overflow-y-auto bg-white border border-stone-200 rounded-sm shadow-lg z-20 p-3 space-y-1.5">
                {EXPORT_COLS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
                    <input type="checkbox" data-testid={`col-${c.key}`} checked={selCols.includes(c.key)} onChange={() => toggleCol(c.key)} /> {c.label}
                  </label>
                ))}
              </div>
            )}
          </div>
          <button data-testid="export-suppliers-btn" onClick={exportExcel} className="rounded-full border border-brand text-brand px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand hover:text-white transition-colors"><FileSpreadsheet size={16} /> Esporta Excel</button>
          <button data-testid="merge-suppliers-btn" onClick={() => setMerging({})} className="rounded-full border border-stone-300 text-stone-600 px-4 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-stone-100 transition-colors"><GitMerge size={16} /> Unisci duplicati</button>
          <button data-testid="new-supplier-btn" onClick={() => setCreating(true)} className="rounded-full bg-brand text-white px-5 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover"><Plus size={16} /> Nuovo fornitore</button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, P.IVA, città, email…" className={`${inp} w-full pl-9`} />
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
            <th className="text-left font-bold py-4 px-6">Ragione sociale</th>
            <th className="text-left font-bold py-4 px-6">P. IVA</th>
            <th className="text-left font-bold py-4 px-6">Città</th>
            <th className="text-left font-bold py-4 px-6">Email</th>
            <th className="text-left font-bold py-4 px-6">Telefono</th>
            <th className="text-right font-bold py-4 px-6">Fatture</th>
            <th className="text-right font-bold py-4 px-6">Spesa</th>
            <th className="py-4 px-6"></th></tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="py-8 px-6 text-center text-stone-400">Nessun fornitore.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.supplier_name} data-testid={`supplier-row-${r.supplier_name}`} className="border-b border-stone-100 hover:bg-stone-50/60">
                <td className="py-4 px-6 font-semibold text-stone-800">{r.supplier_name}
                  {r.source === "manual" && <span className="ml-2 text-xs text-brand bg-brand/10 px-2 py-0.5 rounded-full">manuale</span>}
                  {r.source === "import" && <span className="ml-2 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">import</span>}
                  {dupSet.has(r.supplier_name) && <button data-testid={`dup-badge-${r.supplier_name}`} onClick={() => setMerging({ source: r.supplier_name, target: dupPartner[r.supplier_name] || "" })} title="Possibile duplicato — clicca per unire" className="ml-2 text-xs text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full hover:bg-amber-200 transition-colors">possibile duplicato</button>}</td>
                <td className="py-4 px-6 text-stone-600">{r.vat || "—"}</td>
                <td className="py-4 px-6 text-stone-600">{[r.city, r.province].filter(Boolean).join(" ") || "—"}</td>
                <td className="py-4 px-6 text-stone-600">{r.email || "—"}</td>
                <td className="py-4 px-6 text-stone-600">{r.phone || "—"}</td>
                <td className="py-4 px-6 text-right text-stone-600">{r.invoice_count}</td>
                <td className="py-4 px-6 text-right text-stone-600">{euro(r.total_spent)}</td>
                <td className="py-4 px-6 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <button data-testid={`detail-supplier-${r.supplier_name}`} onClick={() => openDetail(r.supplier_name)} className="text-stone-400 hover:text-brand" title="Scheda fornitore"><BarChart3 size={16} /></button>
                    <button data-testid={`edit-supplier-${r.supplier_name}`} onClick={() => setEdit(r)} className="text-stone-400 hover:text-brand" title="Modifica"><Pencil size={16} /></button>
                    <button data-testid={`reassign-supplier-${r.supplier_name}`} onClick={() => setMerging({ source: r.supplier_name, reassign: true })} className="text-stone-400 hover:text-brand" title="Riassegna prodotti e fatture a un altro fornitore"><ArrowLeftRight size={16} /></button>
                    <button data-testid={`delete-supplier-${r.supplier_name}`} onClick={() => del(r)} className="text-stone-400 hover:text-alert" title="Elimina dall'anagrafica"><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => setDetail(null)}>
          <div data-testid="supplier-detail-dialog" className="bg-white rounded-sm w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <div><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Scheda fornitore</p>
                <h3 className="font-serif text-2xl text-stone-900">{detail.supplier_name}</h3></div>
              <div className="flex items-center gap-3">
                <button data-testid="detail-pdf-btn" onClick={async () => { try { const { data } = await api.get(`/suppliers/${encodeURIComponent(detail.supplier_name)}/detail.pdf`, { responseType: "blob" }); const u = URL.createObjectURL(data); const a = document.createElement("a"); a.href = u; a.download = `scheda_${detail.supplier_name}.pdf`; a.click(); URL.revokeObjectURL(u); } catch { toast.error("Errore generazione PDF"); } }} className="rounded-full border border-brand text-brand px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-brand hover:text-white transition-colors"><Download size={16} /> PDF</button>
                <button onClick={() => setDetail(null)} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <div className="border border-stone-200 rounded-sm p-4"><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Fatture</p><p className="font-serif text-3xl font-light text-stone-900 mt-1">{detail.invoice_count}</p></div>
                <div className="border border-stone-200 rounded-sm p-4"><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Spesa totale</p><p className="font-serif text-3xl font-light text-brand mt-1">{euro(detail.total_spent)}</p></div>
                <div className="border border-stone-200 rounded-sm p-4"><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500">Prodotti</p><p className="font-serif text-3xl font-light text-stone-900 mt-1">{detail.products.length}</p></div>
              </div>
              {detail.monthly_spend.length > 0 && (
                <div><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Spesa mensile</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={detail.monthly_spend}><XAxis dataKey="month" tick={{ fontSize: 10, fill: "#78716C" }} /><YAxis hide /><Tooltip formatter={(v) => `€ ${Number(v).toFixed(2)}`} /><Bar dataKey="total" fill="#2C4A3B" radius={[3, 3, 0, 0]} /></BarChart>
                  </ResponsiveContainer></div>
              )}
              <div><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Prodotti acquistati (storico prezzi)</p>
                {detail.products.length === 0 ? <p className="text-sm text-stone-400">Nessun movimento prezzo registrato per questo fornitore.</p> : (
                  <>
                  <p className="text-xs text-stone-400 mb-2">Clicca un prodotto per vederne l'andamento prezzo.</p>
                  <div className="max-h-64 overflow-y-auto border border-stone-100 rounded-sm"><table className="w-full text-sm">
                    <thead className="sticky top-0 bg-stone-50"><tr className="text-xs uppercase tracking-[0.1em] text-stone-500 border-b border-stone-200">
                      <th className="text-left font-bold py-2 px-3">Prodotto</th><th className="text-right font-bold py-2 px-3">Acquisti</th><th className="text-right font-bold py-2 px-3">Spesa</th><th className="text-right font-bold py-2 px-3">Ultimo €</th><th className="text-right font-bold py-2 px-3">Var.</th></tr></thead>
                    <tbody>{detail.products.map((p) => (
                      <tr key={p.normalized_name} data-testid={`detail-prod-${p.normalized_name}`} onClick={() => setSelProd(p)} className={`border-b border-stone-50 cursor-pointer hover:bg-brand/5 ${selProd?.normalized_name === p.normalized_name ? "bg-brand/5" : ""}`}>
                        <td className="py-2 px-3 text-stone-800">{p.name}</td><td className="py-2 px-3 text-right text-stone-600">{p.purchases}</td><td className="py-2 px-3 text-right text-stone-600">{euro(p.total_spent)}</td><td className="py-2 px-3 text-right text-stone-600">{p.last_price?.toFixed(4)}</td>
                        <td className={`py-2 px-3 text-right font-semibold ${p.change_pct > 0 ? "text-alert" : p.change_pct < 0 ? "text-emerald-600" : "text-stone-400"}`}>{p.change_pct > 0 ? "+" : ""}{p.change_pct}%</td></tr>
                    ))}</tbody></table></div>
                  </>
                )}
              </div>
              {selProd && selProd.series.length > 0 && (
                <div><p className="text-xs uppercase tracking-[0.12em] font-bold text-stone-500 mb-2">Andamento prezzo — {selProd.name}</p>
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart data={selProd.series}><XAxis dataKey="month" tick={{ fontSize: 10, fill: "#78716C" }} /><YAxis domain={["auto", "auto"]} tick={{ fontSize: 10, fill: "#78716C" }} width={44} /><Tooltip formatter={(v) => `€ ${Number(v).toFixed(4)}`} /><Line type="monotone" dataKey="price" stroke="#2C4A3B" strokeWidth={2} dot /></LineChart>
                  </ResponsiveContainer></div>
              )}
            </div>
          </div>
        </div>
      )}

      {(edit || creating) && (
        <SupplierDialog supplier={edit} isNew={creating} onClose={() => { setEdit(null); setCreating(false); }} onSaved={() => { setEdit(null); setCreating(false); load(); }} />
      )}
      {merging && <MergeSuppliersDialog initial={merging} onClose={() => setMerging(false)} onDone={() => { setMerging(false); load(); }} />}
      <ConfirmDialog open={!!confirmDel} title="Elimina fornitore" description={confirmDel ? `Eliminare "${confirmDel.supplier_name}" dall'anagrafica?` : ""} confirmLabel="Elimina" onConfirm={doDel} onClose={() => setConfirmDel(null)} />

      {importPreview && (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={() => !importing && setImportPreview(null)}>
          <div data-testid="import-preview-dialog" className="bg-white rounded-sm w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-stone-200">
              <h3 className="font-serif text-2xl text-stone-900 flex items-center gap-2"><Upload size={20} className="text-brand" /> Anteprima import</h3>
              <button onClick={() => !importing && setImportPreview(null)} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3 text-sm" data-testid="import-preview-counts">
                <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">{importPreview.created} nuovi</span>
                <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold">{importPreview.updated} da aggiornare</span>
                {importPreview.skipped > 0 && <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold">{importPreview.skipped} duplicati</span>}
                <span className="px-3 py-1 rounded-full bg-stone-100 text-stone-600 font-semibold">{importPreview.total} totali</span>
              </div>
              {importPreview.total === 0 ? (
                <p className="text-sm text-alert">Nessuna riga valida trovata nel file. Verifica le intestazioni (scarica il Template).</p>
              ) : (
                <div className="max-h-80 overflow-y-auto border border-stone-100 rounded-sm">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-stone-50"><tr className="text-xs uppercase tracking-[0.12em] text-stone-500 border-b border-stone-200">
                      <th className="text-left font-bold py-2.5 px-4">Ragione sociale</th><th className="text-left font-bold py-2.5 px-4">P. IVA</th>
                      <th className="text-left font-bold py-2.5 px-4">Città</th><th className="text-left font-bold py-2.5 px-4">Azione</th></tr></thead>
                    <tbody>
                      {importPreview.rows.map((r, i) => (
                        <tr key={`${r.supplier_name}-${i}`} data-testid={`import-row-${i}`} className="border-b border-stone-50">
                          <td className="py-2.5 px-4 font-semibold text-stone-800">{r.supplier_name}</td>
                          <td className="py-2.5 px-4 text-stone-600">{r.vat || "—"}</td>
                          <td className="py-2.5 px-4 text-stone-600">{r.city || "—"}</td>
                          <td className="py-2.5 px-4">{r.action === "create"
                            ? <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Nuovo</span>
                            : <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Aggiorna</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
              <button data-testid="import-cancel-btn" onClick={() => setImportPreview(null)} disabled={importing} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100 disabled:opacity-60">Annulla</button>
              <button data-testid="import-confirm-btn" onClick={confirmImport} disabled={importing || importPreview.total === 0} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover disabled:opacity-40 disabled:cursor-not-allowed"><Upload size={16} /> {importing ? "Import in corso…" : "Conferma import"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MergeSuppliersDialog({ onClose, onDone, initial }) {
  const reassign = !!initial?.reassign;
  const [list, setList] = useState([]);
  const [src, setSrc] = useState(initial?.source || "");
  const [tgt, setTgt] = useState(initial?.target || "");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => { if (src) api.get("/suppliers/merge-preview", { params: { source: src } }).then(({ data }) => setPreview(data)).catch(() => setPreview(null)); else setPreview(null); }, [src]);
  useEffect(() => { api.get("/suppliers").then(({ data }) => setList((data || []).map((s) => s.supplier_name))).catch(() => {}); }, []);
  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const doMerge = async () => {
    if (!src || !tgt || src === tgt) { toast.error("Seleziona due fornitori diversi"); return; }
    setBusy(true);
    try { const { data } = await api.post("/suppliers/merge", { source: src, target: tgt }); toast.success(reassign ? `Riassegnati ${data.invoices_moved} fatture e ${data.products_moved} prodotti a "${tgt}". Fornitore "${src}" eliminato.` : `Fornitori uniti: ${data.invoices_moved} fatture, ${data.products_moved} prodotti aggiornati`); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div data-testid="merge-dialog" className="bg-white rounded-sm w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-stone-900 flex items-center gap-2"><GitMerge size={20} className="text-brand" /> {reassign ? "Riassegna fornitore" : "Unisci fornitori duplicati"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-stone-500">{reassign ? "Sposta tutte le fatture e i prodotti di questo fornitore su un altro. Il fornitore di origine verrà eliminato. L'operazione non è reversibile." : "Sposta fatture e prodotti dal fornitore da unire a quello da mantenere. L'operazione non è reversibile."}</p>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{reassign ? "Fornitore di origine (verrà eliminato)" : "Fornitore da unire (verrà eliminato)"}</label>
            <select data-testid="merge-source" disabled={reassign} className={`${inp} mt-1.5 ${reassign ? "bg-stone-100 text-stone-600" : ""}`} value={src} onChange={(e) => setSrc(e.target.value)}>
              <option value="">— seleziona —</option>
              {list.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{reassign ? "Riassegna a" : "Fornitore da mantenere"}</label>
            <select data-testid="merge-target" className={`${inp} mt-1.5`} value={tgt} onChange={(e) => setTgt(e.target.value)}>
              <option value="">— seleziona —</option>
              {list.filter((n) => n !== src).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {src && preview && <p data-testid="merge-preview" className="text-sm text-stone-600 bg-stone-50 border border-stone-200 rounded-sm px-3 py-2">Verranno spostate <b>{preview.invoices}</b> fatture e <b>{preview.products}</b> prodotti sul fornitore da mantenere.</p>}
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100 rounded-sm">Annulla</button>
          <button data-testid="merge-confirm" onClick={doMerge} disabled={busy || !src || !tgt || src === tgt} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-hover transition-colors disabled:opacity-40">{busy ? (reassign ? "Riassegnazione…" : "Unione…") : (reassign ? "Riassegna ed elimina" : "Unisci")}</button>
        </div>
      </div>
    </div>
  );
}

function SupplierDialog({ supplier, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    const f = {};
    FIELDS.forEach((x) => { f[x.key] = (supplier && supplier[x.key]) || ""; });
    if (!f.country) f.country = "Italia";
    return f;
  });
  const [saving, setSaving] = useState(false);
  const [existing, setExisting] = useState([]);
  useEffect(() => { api.get("/suppliers").then(({ data }) => setExisting((data || []).map((s) => s.supplier_name))).catch(() => {}); }, []);
  const trimmedName = (form.supplier_name || "").trim().toLowerCase();
  const dup = !!trimmedName && trimmedName !== (supplier?.supplier_name || "").toLowerCase() && existing.some((o) => (o || "").toLowerCase() === trimmedName);
  const inp = "w-full border border-stone-300 bg-white px-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40";
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    const name = (form.supplier_name || "").trim();
    if (!name) { toast.error("La ragione sociale è obbligatoria"); return; }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/suppliers", form);
      } else {
        await api.put(`/suppliers/${encodeURIComponent(supplier.supplier_name)}/meta`, form);
      }
      toast.success(isNew ? "Fornitore creato" : "Fornitore aggiornato");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div data-testid="supplier-dialog" className="bg-white rounded-sm w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <h3 className="font-serif text-2xl text-stone-900 flex items-center gap-2"><Truck size={20} className="text-brand" /> {isNew ? "Nuovo fornitore" : "Modifica fornitore"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-800"><X size={20} /></button>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((x) => {
            const val = form[x.key];
            const invalid = (x.key === "vat" && !isValidVatIT(val)) || (x.key === "iban" && !isValidIBAN(val));
            return (
              <div key={x.key} className={x.full ? "md:col-span-2" : ""}>
                <label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">{x.label}{x.required && " *"}</label>
                <input data-testid={`sup-${x.key}`} type={x.type || "text"}
                  className={`${inp} mt-1.5 ${invalid ? "border-alert ring-1 ring-alert/40" : ""}`}
                  value={val} onChange={(e) => set(x.key, e.target.value)} />
                {invalid && <p data-testid={`sup-${x.key}-error`} className="text-xs text-alert mt-1">{x.key === "vat" ? "P. IVA non valida (11 cifre, checksum)" : "IBAN non valido"}</p>}
                {x.key === "supplier_name" && dup && <p data-testid="sup-dup-warning" className="text-xs text-amber-700 mt-1">Attenzione: esiste già un fornitore con questo nome.</p>}
              </div>
            );
          })}
        </div>
        <div className="p-6 border-t border-stone-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-stone-600 hover:bg-stone-100">Annulla</button>
          <button data-testid="supplier-save-btn" onClick={save} disabled={saving} className="rounded-full bg-brand text-white px-6 py-2.5 text-sm font-semibold flex items-center gap-2 hover:bg-brand-hover disabled:opacity-60"><Save size={16} /> {saving ? "Salvataggio…" : "Salva"}</button>
        </div>
      </div>
    </div>
  );
}
